/**
 * Maps ISO 3166-1 alpha-3 country codes to alpha-2 codes
 * Also handles special cases and regional codes
 * This is a comprehensive mapping for all codes used in the application
 */
const alpha3ToAlpha2: Record<string, string> = {
  // Standard ISO 3166-1 alpha-3 to alpha-2 mappings
  ARG: 'AR', // Argentina
  AUS: 'AU', // Australia
  AUT: 'AT', // Austria
  BEL: 'BE', // Belgium
  BMU: 'BM', // Bermuda
  BRA: 'BR', // Brazil
  CAN: 'CA', // Canada
  CHL: 'CL', // Chile
  CHN: 'CN', // China
  COL: 'CO', // Colombia
  CYM: 'KY', // Cayman Islands
  CZE: 'CZ', // Czech Republic
  DEU: 'DE', // Germany
  DNK: 'DK', // Denmark
  EGY: 'EG', // Egypt
  ESP: 'ES', // Spain
  EST: 'EE', // Estonia
  FIN: 'FI', // Finland
  FRA: 'FR', // France
  HUN: 'HU', // Hungary
  IDN: 'ID', // Indonesia
  IND: 'IN', // India
  ISR: 'IL', // Israel
  ITA: 'IT', // Italy
  JPN: 'JP', // Japan
  JAP: 'JP', // Alternative code for Japan
  KOR: 'KR', // South Korea
  LKA: 'LK', // Sri Lanka
  LTU: 'LT', // Lithuania
  LVA: 'LV', // Latvia
  MEX: 'MX', // Mexico
  MYS: 'MY', // Malaysia
  NLD: 'NL', // Netherlands
  NOR: 'NO', // Norway
  NZL: 'NZ', // New Zealand
  PER: 'PE', // Peru
  PHL: 'PH', // Philippines
  POL: 'PL', // Poland
  ROU: 'RO', // Romania
  RUS: 'RU', // Russia
  SAU: 'SA', // Saudi Arabia
  SGP: 'SG', // Singapore
  SVK: 'SK', // Slovakia
  SVN: 'SI', // Slovenia
  SWE: 'SE', // Sweden
  THA: 'TH', // Thailand
  TUR: 'TR', // Turkey
  TWN: 'TW', // Taiwan
  UAE: 'AE', // United Arab Emirates
  VNM: 'VN', // Vietnam
  ZAF: 'ZA', // South Africa
  // Special cases
  UK: 'GB', // United Kingdom (not standard ISO, but used in codebase)
  EUR: 'EU', // European Union (not a country, but used in codebase)
};

/**
 * Regional codes that don't have standard flag representations
 * These will be handled specially
 */
const regionalCodes = ['EU', 'EM', 'LATAM', 'ASIA', 'ASIAXJ', 'WOR'];

/**
 * Converts a country code (alpha-2, alpha-3, or regional) to alpha-2 format
 * for use with flag-icons package
 * @param countryCode - Country code in any format
 * @returns Alpha-2 code or null if invalid/regional
 */
export const convertCountryCodeToAlpha2 = (
  countryCode: string
): string | null => {
  const upperCode = countryCode.toUpperCase();

  // Already alpha-2 (2 characters)
  if (upperCode.length === 2) {
    // Check if it's a regional code
    if (regionalCodes.includes(upperCode)) {
      return null; // Regional codes don't have standard flags
    }

    return upperCode;
  }

  // Alpha-3 (3 characters)
  if (upperCode.length === 3) {
    return alpha3ToAlpha2[upperCode] || null;
  }

  return null;
};

/**
 * Checks if a country code is a regional code
 */
export const isRegionalCode = (countryCode: string): boolean => {
  return regionalCodes.includes(countryCode.toUpperCase());
};
